import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaArrowRight, FaChevronRight } from "react-icons/fa";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { _imagesURL } from "../../constants/API";
import { NEW_PROFILE_DOCTOR } from "../../constants/RoutesUrl";
import { Language } from "../../App";
import { IoClose } from "react-icons/io5";
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
import { fetchPaypalToken, createDropInInstance } from "../../services/paypalApi";
import { createKlarnaCheckoutSession, getKlarnaCheckoutSessionStatus } from "../../services/stripeApi";
import { CheckoutProvider } from "../payments/klarna-stripe/CheckoutProvider";
import { CheckoutForm } from "../payments/klarna-stripe/CheckoutForm";
import { SessionStatus } from "../payments/klarna-stripe/SessionStatus";
import AgendaPaymentDropInModal from "../agenda/common/AgendaPaymentDropInModal";
import {
  Specialist3DIcon,
  Users3DIcon,
  Fee3DIcon,
  Check3DIcon,
} from "../agenda/common/Agenda3DIcons";

async function fetchMentorProfile(userId) {
  const res = await apiAdmin.post("user/mentor/profile", { user_id: userId });
  return res.data;
}

const FALLBACK_ACCOUNT_DETAILS = {
  fullName: "Dr. Jane Smith",
  workEmail: "janesmith@hospital.com",
  image: placeholderAvatar,
  id: null,
};

function getAccountDetails() {
  try {
    const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
    if (!user) return FALLBACK_ACCOUNT_DETAILS;
    return {
      fullName:
        user.name ||
        [user.firstname, user.lastname].filter(Boolean).join(" ") ||
        FALLBACK_ACCOUNT_DETAILS.fullName,
      workEmail: user.email || FALLBACK_ACCOUNT_DETAILS.workEmail,
      image: resolveMentorImage(user.image),
      id: user.id ?? FALLBACK_ACCOUNT_DETAILS.id,
    };
  } catch (e) {
    return FALLBACK_ACCOUNT_DETAILS;
  }
}

const UPLOAD_DELAY_THRESHOLD_MS = 6000;

function SelectedBadge() {
  return (
    <span
      className="mam-selected-badge"
      style={{
        position: "absolute",
        top: "-8px",
        right: "8px",
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #00C8E8 0%, #00BADA 100%)",
        border: "2px solid #FFFFFF",
        boxShadow: "0 2px 8px rgba(0, 186, 218, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      <svg width="11" height="9" viewBox="0 0 10 8" fill="none">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="#FFFFFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function CvFileIcon() {
  return (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #E0F7FA 0%, #D0F1F7 100%)",
        border: "1px solid #9BE5F3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 2px 6px rgba(0, 186, 218, 0.15)",
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6Z"
          stroke="#0891B2"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M13 2v5a1 1 0 0 0 1 1h5"
          stroke="#0891B2"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function CvDelayedIcon() {
  return (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #FEF3E2 0%, #FEEAD0 100%)",
        border: "1px solid #FCD39A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#B45309" strokeWidth="1.8" />
        <path
          d="M12 7v5l3 2"
          stroke="#B45309"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function SpinnerIcon({ color = "#00BADA", size = 15 }) {
  return (
    <>
      <style>{`@keyframes cvUploadSpin{to{transform:rotate(360deg)}}`}</style>
      <span
        style={{
          display: "inline-block",
          width: `${size}px`,
          height: `${size}px`,
          border: `2px solid ${color}33`,
          borderTopColor: color,
          borderRadius: "50%",
          animation: "cvUploadSpin 0.7s linear infinite",
          flexShrink: 0,
        }}
      />
    </>
  );
}

function CancelIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke="#5F6673"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatUploadedAgo(uploadedAt, lang) {
  if (!uploadedAt) return "";
  const seconds = Math.floor((Date.now() - uploadedAt) / 1000);
  const prefixTemplate =
    lang?.mentorship_uploaded_time_template || "Uploaded {{time}}";
  let timeStr;
  if (seconds < 45) {
    timeStr = lang?.just_now || "Just now";
  } else {
    const minutes = Math.floor(seconds / 60);
    let duration;
    if (minutes < 60) {
      duration = `${minutes} ${minutes > 1 ? lang?.mins || "mins" : lang?.min || "min"}`;
    } else {
      const hours = Math.floor(minutes / 60);
      if (hours < 24) {
        duration = `${hours} ${hours > 1 ? lang?.hours || "hours" : lang?.hour || "hour"}`;
      } else {
        const days = Math.floor(hours / 24);
        duration = `${days} ${days > 1 ? lang?.days || "days" : lang?.day || "day"}`;
      }
    }
    const agoTemplate = lang?.mentorship_time_ago_template || "{{time}} ago";
    timeStr = agoTemplate.replace("{{time}}", duration);
  }
  return prefixTemplate.replace("{{time}}", timeStr);
}

function mentorSummary(mentor) {
  if (!mentor) return "";
  return [mentor.specialty, mentor.institution, mentor.institutionSub]
    .filter(Boolean)
    .join(" · ");
}

function isPdfFile(file) {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
}

function MentorshipApplicationModal({ isOpen, toggle, mentor, onSubmitted }) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const [checked1, setChecked1] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("paypal");
  const [dragOver, setDragOver] = useState(false);
  const [mentorProfileData, setMentorProfileData] = useState(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [dropInModal, setDropInModal] = useState(false);
  const [dropInInstance, setDropInInstance] = useState(null);
  const [canPay, setCanPay] = useState(false);
  const [dropInInitializing, setDropInInitializing] = useState(false);
  const [klarnaModal, setKlarnaModal] = useState(false);
  const [klarnaClientSecret, setKlarnaClientSecret] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [sessionPaymentStatus, setSessionPaymentStatus] = useState(null);
  const accountDetails = getAccountDetails();

  useEffect(() => {
    if (!isOpen || !mentor?.id) {
      setMentorProfileData(null);
      return;
    }
    let cancelled = false;
    fetchMentorProfile(mentor.id)
      .then((data) => {
        if (!cancelled && data?.status)
          setMentorProfileData(data.data?.mentor || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, mentor?.id]);

  const mentorProfile = mentorProfileData?.mentor_profile;
  const showCvUpload = mentorProfile?.is_receive_data === 1;

  const mentorDisplayInfo = {
    ...mentor,
    specialty:
      mentorProfile?.specialty ||
      mentorProfileData?.category ||
      mentorProfileData?.subcategory ||
      mentor?.specialty,
    institution: mentorProfile?.institute || mentor?.institution,
  };
  const cvDropzoneText =
    showCvUpload && mentorProfile?.receive_data_title
      ? mentorProfile.receive_data_title
      : null;

  const [showBookedModal, setShowBookedModal] = useState(false);

  const [cvFile, setCvFile] = useState(null);
  const [cvUploadState, setCvUploadState] = useState("idle");
  const [uploadedAt, setUploadedAt] = useState(null);
  const [, forceTick] = useState(0);

  const delayTimerRef = useRef(null);
  const uploadTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);

  const clearUploadTimers = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (uploadTimerRef.current) {
      clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearUploadTimers();
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setChecked1(false);
    setShowValidation(false);
    setSelectedPayment("paypal");
    clearUploadTimers();
    setCvFile(null);
    setCvUploadState("idle");
    setUploadedAt(null);
    setDragOver(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (cvUploadState !== "uploaded") return;
    tickIntervalRef.current = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(tickIntervalRef.current);
  }, [cvUploadState]);

  const uploadCvFile = (file) => {
    return new Promise((resolve) => {
      const duration = 1500 + Math.random() * 2000;
      uploadTimerRef.current = setTimeout(resolve, duration);
    });
  };

  const startUpload = (file) => {
    if (!file) return;
    clearUploadTimers();
    setCvFile(file);
    setCvUploadState("uploading");
    setUploadedAt(null);

    delayTimerRef.current = setTimeout(() => {
      setCvUploadState((prev) => (prev === "uploading" ? "delayed" : prev));
    }, UPLOAD_DELAY_THRESHOLD_MS);

    uploadCvFile(file).then(() => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setCvUploadState("uploaded");
      setUploadedAt(Date.now());
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error(lang?.please_select_pdf_file || "Please select pdf file*");
      return;
    }
    startUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (cvUploadState !== "idle") return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error(lang?.please_select_pdf_file || "Please select pdf file*");
      return;
    }
    startUpload(file);
  };

  const handleCancelUpload = () => {
    clearUploadTimers();
    setCvFile(null);
    setCvUploadState("idle");
    setUploadedAt(null);
  };

  const handleDeleteCv = () => {
    clearUploadTimers();
    setCvFile(null);
    setCvUploadState("idle");
    setUploadedAt(null);
  };

  const [isClosing, setIsClosing] = useState(false);
  const [isBookedClosing, setIsBookedClosing] = useState(false);

  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  const closeBookedModal = (callback) => {
    setIsBookedClosing(true);
    setTimeout(() => {
      setShowBookedModal(false);
      setIsBookedClosing(false);
      callback?.();
    }, 800);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (showBookedModal) closeBookedModal();
      else if (isOpen) handleClose();
    };
    if (isOpen || showBookedModal) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showBookedModal]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const pending = localStorage.getItem("mam_pending_request");
    if (!sessionId || !pending || !mentor?.id) return;

    setProcessingPayment(true);
    getKlarnaCheckoutSessionStatus(sessionId)
      .then((res) => {
        const sessionData = res?.data?.data ?? res?.data;
        const paymentStatus = sessionData?.payment_status ?? res?.payment_status;
        setSessionPaymentStatus(paymentStatus);
        if (paymentStatus === "paid") {
          return submitMentorRequest({ paymentType: "stripe", nonce: sessionId });
        }
        throw new Error(`Payment was not completed (status: ${paymentStatus ?? "unknown"}).`);
      })
      .catch((err) => {
        console.error("Klarna session verification error:", err);
        setSessionError(err?.response?.data?.message || err.message || (lang?.payment_verify_failed || "Failed to verify payment."));
        setProcessingPayment(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentor?.id]);

  if (!mentor) return null;

  const mentorPrice = Number(mentorProfile?.price ?? mentor.mentor_profile?.price ?? 0) || 0;
  const cvMissing = showCvUpload && cvUploadState !== "uploaded";
  const checkboxMissing = !checked1;

  const submitMentorRequest = async (paymentMeta) => {
    setSubmittingRequest(true);
    try {
      const fd = new FormData();
      fd.append("mentor_id", mentor.id);
      fd.append("mentorship_type", "One-To-One");
      if (cvUploadState === "uploaded" && cvFile) {
        fd.append("data", cvFile);
      }
      if (paymentMeta?.paymentType) fd.append("payment_type", paymentMeta.paymentType);
      if (paymentMeta?.nonce) fd.append("nonce", paymentMeta.nonce);
      const res = await apiAdmin.post("user/mentor/request", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.status) {
        localStorage.removeItem("mam_pending_request");
        onSubmitted?.(mentor);
        setDropInModal(false);
        setKlarnaModal(false);
        closeThen(() => setShowBookedModal(true));
      } else {
        toast.error(
          getApiErrorMessage(
            res.data,
            lang?.mentorship_error_submit_request ||
              "Couldn't submit your request. Please try again.",
          ),
        );
      }
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err.response?.data,
          lang?.mentorship_error_submit_request ||
            "Couldn't submit your request. Please try again.",
        ),
      );
    } finally {
      setSubmittingRequest(false);
      setProcessingPayment(false);
    }
  };

  const handlePaypalPayment = async () => {
    if (mentorPrice <= 0) {
      setProcessingPayment(true);
      await submitMentorRequest({ paymentType: "paypal" });
      return;
    }
    setDropInInitializing(true);
    setCanPay(false);
    setDropInInstance(null);
    setDropInModal(true);
    try {
      const userToken = await fetchPaypalToken();
      const instance = await createDropInInstance(userToken, "dropin-container", mentorPrice.toFixed(2));
      instance.on("paymentMethodRequestable", () => setCanPay(true));
      instance.on("noPaymentMethodRequestable", () => setCanPay(false));
      setDropInInstance(instance);
    } catch (err) {
      console.error("PayPal payment error:", err);
      toast.error(lang?.mentorship_paypal_payment_error || "Couldn't process PayPal payment. Please try again.");
      setDropInModal(false);
    } finally {
      setDropInInitializing(false);
    }
  };

  const handleDropInPay = () => {
    if (!dropInInstance || !canPay || processingPayment) return;
    dropInInstance.requestPaymentMethod((err, payload) => {
      if (err) {
        console.error("Error requesting payment method:", err);
        toast.error(lang?.mentorship_paypal_payment_error || "Couldn't process PayPal payment. Please try again.");
        return;
      }
      setProcessingPayment(true);
      submitMentorRequest({ paymentType: "paypal", nonce: payload.nonce });
    });
  };

  const handleKlarnaPayment = async () => {
    setProcessingPayment(true);
    setSessionError(null);
    try {
      if (mentorPrice > 0) {
        localStorage.setItem("mam_pending_request", JSON.stringify({ mentorId: mentor.id }));
        const amount = Math.round(mentorPrice * 100);
        const response = await createKlarnaCheckoutSession({
          courseId: mentor.id,
          amount,
          title: `Mentorship with ${mentor.name}`,
        });
        const clientSecret = response?.data?.client_secret ?? response?.client_secret;
        if (clientSecret) {
          setKlarnaClientSecret(clientSecret);
          setKlarnaModal(true);
        } else {
          throw new Error(lang?.klarna_init_error || "Could not initialize Klarna checkout.");
        }
      } else {
        await submitMentorRequest({ paymentType: "stripe" });
      }
    } catch (err) {
      console.error("Klarna payment error:", err);
      toast.error(lang?.mentorship_klarna_payment_error || "Couldn't start Klarna checkout. Please try again.");
      setProcessingPayment(false);
    }
  };

  const handleContinue = () => {
    if (cvMissing || checkboxMissing) {
      setShowValidation(true);
      return;
    }
    if (selectedPayment === "klarna") {
      handleKlarnaPayment();
    } else {
      handlePaypalPayment();
    }
  };

  return (
    <>
      <style>{`
      .mam-curtain-backdrop {
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1050;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        animation: mamBackdropFadeIn 0.8s ease-out forwards;
      }
      .mam-curtain-backdrop.closing {
        animation: mamBackdropFadeOut 0.8s ease-out forwards;
      }
      .mam-curtain-content {
        position: relative;
        width: 100%;
        max-width: 550px;
        max-height: 88vh;
        overflow-y: auto;
        background: #FFFFFF;
        border: 0.5px solid #E6E7EA;
        border-radius: 26px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        transform: translateY(-100vh);
        animation: mamCurtainOpen 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .mam-curtain-content::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }
      .mbk-curtain-content {
        position: relative;
        width: 100%;
        max-width: 500px;
        max-height: 88vh;
        overflow-y: auto;
        background: #FFFFFF;
        border: 0.5px solid #E6E7EA;
        border-radius: 26px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        transform: translateY(-100vh);
        animation: mamCurtainOpen 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .mbk-curtain-content::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }
      .mam-curtain-content.closing,
      .mbk-curtain-content.closing {
        animation: mamCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
      }
      .mam-close-btn {
        position: absolute;
        top: 18px;
        right: 20px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: 1px solid #BCEAF2;
        box-shadow: 0 2px 8px rgba(0, 186, 218, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0E7490;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: 30;
      }
      .mam-close-btn:hover {
        background: #00BADA;
        color: #FFFFFF;
        border-color: #00BADA;
        transform: rotate(90deg) scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 186, 218, 0.35);
      }
      .mam-mentor-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(135deg, #CEF4FA 0%, #A2ECF8 45%, #75E1F4 100%);
        border: 2px solid #00BADA;
        border-radius: 16px;
        padding: 12px 16px;
        margin-bottom: 14px;
        box-shadow: 0 4px 16px rgba(0, 186, 218, 0.25), 0 0 0 1px rgba(0, 200, 232, 0.15);
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
      }
      .mam-mentor-card::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -20%;
        width: 120px;
        height: 120px;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.45) 0%, transparent 70%);
        pointer-events: none;
      }
      .mam-mentor-card:hover {
        border-color: #008AA8;
        background: linear-gradient(135deg, #BCEFF8 0%, #90E6F6 45%, #60DBF2 100%);
        box-shadow: 0 8px 26px rgba(0, 186, 218, 0.42), 0 0 0 1.5px rgba(0, 186, 218, 0.4);
        transform: translateY(-2px);
      }
      .mam-mentor-avatar-wrap {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        padding: 2.5px;
        background: linear-gradient(135deg, #00F0FF 0%, #00BADA 50%, #0284C7 100%);
        box-shadow: 0 4px 12px rgba(0, 186, 218, 0.4);
        flex-shrink: 0;
      }
      .mam-mentor-avatar {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
        display: block;
        border: 2px solid #FFFFFF;
      }
      .mam-mentor-chevron {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #00C8E8 0%, #00BADA 100%);
        color: #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 3px 10px rgba(0, 186, 218, 0.45);
        transition: all 0.25s ease;
        border: 1.5px solid rgba(255, 255, 255, 0.6);
        z-index: 1;
      }
      .mam-mentor-card:hover .mam-mentor-chevron {
        background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 100%);
        transform: translateX(3px) scale(1.08);
        box-shadow: 0 6px 16px rgba(0, 186, 218, 0.6);
      }
      .mam-details-card {
        background: linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%);
        border: 1.5px solid #B4E8F2;
        border-radius: 16px;
        padding: 12px 16px;
        margin-bottom: 14px;
        box-shadow: 0 2px 10px rgba(0, 186, 218, 0.08);
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 13px;
      }
      .mam-details-card:hover {
        border-color: #00BADA;
        box-shadow: 0 4px 16px rgba(0, 186, 218, 0.15);
      }
      .mam-details-card.clickable {
        cursor: pointer;
      }
      .mam-details-card.clickable:hover {
        transform: translateY(-1px);
      }
      .mam-user-avatar-wrap {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        padding: 2px;
        background: linear-gradient(135deg, #38BDF8 0%, #0284C7 100%);
        box-shadow: 0 2px 8px rgba(2, 132, 199, 0.25);
        flex-shrink: 0;
      }
      .mam-user-avatar {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
        display: block;
        border: 2px solid #FFFFFF;
      }
      .mam-pay-option {
        border-radius: 16px;
        padding: 13px 18px;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .mam-pay-option:hover {
        transform: translateY(-1.5px);
      }
      .mam-cta-btn {
        width: 100%;
        height: 48px;
        border-radius: 999px;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        font-size: 15px;
        font-weight: 750;
        letter-spacing: 0.2px;
        color: #FFFFFF;
        background: linear-gradient(135deg, #00C8E8 0%, #00BADA 50%, #0088B3 100%);
        box-shadow: 0 6px 22px rgba(0, 186, 218, 0.4);
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
      }
      .mam-cta-btn:hover:not(:disabled) {
        background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 50%, #0099C8 100%);
        box-shadow: 0 8px 26px rgba(0, 186, 218, 0.55);
        transform: translateY(-1.5px);
      }
      .mam-cta-btn:active:not(:disabled) {
        transform: translateY(0.5px);
        box-shadow: 0 4px 14px rgba(0, 186, 218, 0.28);
      }
      .mam-checkbox-box {
        width: 20px;
        height: 20px;
        border-radius: 6px;
        flex-shrink: 0;
        margin-top: 1px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.18s ease;
      }
      @keyframes mamBackdropFadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes mamBackdropFadeOut {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes mamCurtainOpen {
        0% { transform: translateY(-100vh); }
        100% { transform: translateY(0); }
      }
      @keyframes mamCurtainClose {
        0% { transform: translateY(0); }
        100% { transform: translateY(-100vh); }
      }
      @media (prefers-reduced-motion: reduce) {
        .mam-curtain-backdrop, .mam-curtain-content, .mbk-curtain-content {
          animation: none !important;
        }
      }
    `}</style>
      {(isOpen || isClosing) && (
        <div
          className={`mam-curtain-backdrop ${isClosing ? "closing" : ""}`}
          onClick={handleClose}
        >
          <div
            className={`mam-curtain-content ${isClosing ? "closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="position-relative" style={{ padding: "26px 24px 28px" }}>
              {/* Close button */}
              <button
                type="button"
                onClick={handleClose}
                className="mam-close-btn"
                aria-label="Close modal"
              >
                <IoClose size={18} />
              </button>

              {/* Header */}
              <h5
                style={{
                  fontWeight: 800,
                  fontSize: "21px",
                  color: "#0F1A2B",
                  margin: "0 0 4px",
                  paddingRight: "36px",
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                {lang?.mentorship_apply_for_mentorship_title ||
                  "Apply for mentorship"}
              </h5>
              <p
                style={{
                  color: "#475569",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  margin: "0 0 18px",
                  lineHeight: 1.5,
                }}
              >
                {lang?.mentorship_apply_modal_subtitle ||
                  "Review your details, then choose how you'd like to pay."}
              </p>

              {/* SELECTED MENTOR */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <Specialist3DIcon size={16} />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "#0891B2",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {lang?.mentorship_selected_mentor_label || "SELECTED MENTOR"}
                </span>
              </div>

              <div
                onClick={() => closeThen(() => navigate(`${NEW_PROFILE_DOCTOR}/${mentor.user_name || mentor.id}`)) }
                className="mam-mentor-card mb-3"
              >
                <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, zIndex: 1 }}>
                  <div className="mam-mentor-avatar-wrap">
                    <img
                      src={mentor.image || placeholderAvatar}
                      alt={mentor.name}
                      onError={(e) => {
                        if (e.target.src !== placeholderAvatar) {
                          e.target.src = placeholderAvatar;
                        }
                      }}
                      className="mam-mentor-avatar"
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p
                      className="text-truncate mb-0"
                      style={{
                        fontWeight: 850,
                        fontSize: "15px",
                        color: "#063943",
                        lineHeight: 1.25,
                        letterSpacing: "-0.01em",
                      }}
                      title={mentor.name}
                    >
                      {mentor.name}
                    </p>
                    <p
                      className="text-truncate mb-0"
                      style={{
                        fontSize: "12px",
                        color: "#006D85",
                        fontWeight: 600,
                        lineHeight: 1.35,
                      }}
                      title={mentorSummary(mentorDisplayInfo)}
                    >
                      {mentorSummary(mentorDisplayInfo)}
                    </p>
                  </div>
                </div>
                <div className="mam-mentor-chevron">
                  <FaChevronRight size={11} />
                </div>
              </div>

              {/* YOUR DETAILS */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <Users3DIcon size={16} />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "#0891B2",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {lang?.mentorship_your_details_label || "YOUR DETAILS"}
                </span>
              </div>

              <div
                onClick={
                  accountDetails.id
                    ? () => closeThen(() => navigate(`${NEW_PROFILE_DOCTOR}/${accountDetails.user_name || accountDetails.id}`))
                    : undefined
                }
                className={`mam-details-card mb-3 ${accountDetails.id ? "clickable" : ""}`}
              >
                <div className="mam-user-avatar-wrap">
                  <img
                    src={accountDetails.image || placeholderAvatar}
                    alt={accountDetails.fullName}
                    onError={(e) => {
                      if (e.target.src !== placeholderAvatar) {
                        e.target.src = placeholderAvatar;
                      }
                    }}
                    className="mam-user-avatar"
                  />
                </div>
                <div style={{ minWidth: 0, flexGrow: 1 }}>
                  <p
                    className="text-truncate mb-0"
                    style={{
                      fontWeight: 800,
                      fontSize: "14.5px",
                      color: "#0F1A2B",
                      lineHeight: 1.25,
                    }}
                    title={accountDetails.fullName}
                  >
                    {accountDetails.fullName}
                  </p>
                  <p
                    className="text-truncate mb-0"
                    style={{ fontSize: "12px", color: "#475569", fontWeight: 550, lineHeight: 1.35 }}
                    title={accountDetails.workEmail}
                  >
                    {accountDetails.workEmail}
                  </p>
                </div>
              </div>

              {/* UPLOAD YOUR CV — only when the mentor has this enabled in settings */}
              {showCvUpload && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 800,
                        color: "#0891B2",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {lang?.mentorship_upload_cv_label || "UPLOAD YOUR CV"}
                    </span>
                  </div>

                  {cvUploadState === "idle" ? (
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById("mamCvInput")?.click()
                      }
                      style={{
                        border: `1.5px dashed ${
                          showValidation && cvMissing
                            ? "#EF4444"
                            : dragOver
                              ? "#00BADA"
                              : "#00BADA"
                        }`,
                        borderRadius: "16px",
                        padding: "20px 16px",
                        textAlign: "center",
                        marginBottom:
                          showValidation && cvMissing ? "6px" : "14px",
                        background: dragOver
                          ? "linear-gradient(145deg, #E0F7FA 0%, #D4F2F8 100%)"
                          : "linear-gradient(145deg, #F0FBFD 0%, #E6F8FB 100%)",
                        boxShadow: "0 2px 8px rgba(0, 186, 218, 0.08)",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "13px",
                          fontWeight: 650,
                          color: "#0F1A2B",
                        }}
                      >
                        {cvDropzoneText || (
                          <>
                            {lang?.mentorship_drag_drop_cv_prefix ||
                              "Drag & drop your CV here, or"}{" "}
                            <span
                              style={{
                                color: "#00BADA",
                                textDecoration: "underline",
                                fontWeight: 800,
                              }}
                            >
                              {lang?.mentorship_browse_files || "browse files"}
                            </span>
                          </>
                        )}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#475569",
                        }}
                      >
                        {lang?.mentorship_pdf_format || "PDF format"}
                      </p>
                      <input
                        id="mamCvInput"
                        type="file"
                        accept=".pdf"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </div>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-between mb-3"
                      style={{
                        border: `1.5px solid ${cvUploadState === "delayed" ? "#FCD39A" : "#00BADA"}`,
                        borderRadius: "14px",
                        padding: "11px 14px",
                        gap: "12px",
                        background: cvUploadState === "delayed"
                          ? "linear-gradient(135deg, #FFFDF5 0%, #FEF3E2 100%)"
                          : "linear-gradient(135deg, #E0F8FA 0%, #C6F1F8 100%)",
                        boxShadow: "0 4px 16px rgba(0, 186, 218, 0.18)",
                      }}
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "12px", minWidth: 0 }}
                      >
                        {cvUploadState === "delayed" ? (
                          <CvDelayedIcon />
                        ) : (
                          <CvFileIcon />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <p
                            style={{
                              margin: "0 0 2px",
                              fontSize: "13px",
                              fontWeight: 750,
                              color: "#0F1A2B",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={cvFile?.name}
                          >
                            {cvFile?.name}
                          </p>
                          <p
                            className="d-flex align-items-center mb-0"
                            style={{
                              fontSize: "11px",
                              color: "#475569",
                              gap: "6px",
                              fontWeight: 600,
                            }}
                          >
                            {cvUploadState === "uploading" && (
                              <>
                                <SpinnerIcon color="#00BADA" size={13} />
                                <span style={{ color: "#0891B2" }}>
                                  {lang?.mentorship_pdf_format || "PDF format"}{" "}
                                  &nbsp;•&nbsp;{" "}
                                  {lang?.uploading || "Uploading…"}
                                </span>
                              </>
                            )}
                            {cvUploadState === "delayed" && (
                              <>
                                <SpinnerIcon color="#B45309" size={13} />
                                <span style={{ color: "#B45309" }}>
                                  {lang?.mentorship_cv_upload_delayed ||
                                    "Still uploading — this is taking longer than usual"}
                                </span>
                              </>
                            )}
                            {cvUploadState === "uploaded" && (
                              <span style={{ color: "#065F70" }}>
                                {lang?.mentorship_pdf_format || "PDF format"}{" "}
                                &nbsp;•&nbsp;{" "}
                                {formatUploadedAgo(uploadedAt, lang)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {(cvUploadState === "uploading" ||
                        cvUploadState === "delayed") && (
                        <button
                          type="button"
                          onClick={handleCancelUpload}
                          title={
                            lang?.mentorship_cancel_upload_title ||
                            "Cancel upload"
                          }
                          className="d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background: "rgba(255, 255, 255, 0.9)",
                            color: "#475569",
                            flexShrink: 0,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                          }}
                        >
                          <CancelIcon />
                        </button>
                      )}

                      {cvUploadState === "uploaded" && (
                        <button
                          type="button"
                          onClick={handleDeleteCv}
                          title={
                            lang?.mentorship_remove_pdf_title || "Remove PDF"
                          }
                          className="d-flex align-items-center justify-content-center border-0"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background: "#FEE2E2",
                            color: "#EF4444",
                            flexShrink: 0,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            boxShadow: "0 2px 6px rgba(239, 68, 68, 0.2)",
                          }}
                        >
                          <RiDeleteBin6Line size={15} />
                        </button>
                      )}
                    </div>
                  )}

                  {showValidation && cvMissing && (
                    <p
                      style={{
                        margin: "0 0 14px",
                        fontSize: "11.5px",
                        fontWeight: 650,
                        color: "#EF4444",
                      }}
                    >
                      {lang?.mentorship_cv_required_error ||
                        "Please upload your CV as a PDF to continue."}
                    </p>
                  )}
                </>
              )}

              {/* PAYMENT METHOD */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <Fee3DIcon size={16} />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "#0891B2",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {lang?.mentorship_payment_method_label || "PAYMENT METHOD"}
                </span>
              </div>

              {/* PayPal option */}
              <div
                onClick={() => setSelectedPayment("paypal")}
                className="mam-pay-option mb-2"
                style={{
                  border: `2px solid ${selectedPayment === "paypal" ? "#0079C1" : "#F2DC9B"}`,
                  background: selectedPayment === "paypal"
                    ? "linear-gradient(135deg, #FFF6D6 0%, #FFEDB3 100%)"
                    : "#FFFDF2",
                  boxShadow: selectedPayment === "paypal"
                    ? "0 4px 18px rgba(0, 121, 193, 0.2)"
                    : "0 2px 6px rgba(0, 0, 0, 0.03)",
                }}
              >
                {selectedPayment === "paypal" && <SelectedBadge />}
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "13.5px",
                    color: "#0F1A2B",
                  }}
                >
                  {lang?.mentorship_pay_with_card_paypal ||
                    "Pay with card or PayPal"}
                </span>
                <span
                  style={{
                    fontWeight: 850,
                    fontSize: "15px",
                    fontStyle: "italic",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <span style={{ color: "#003087" }}>Pay</span>
                  <span style={{ color: "#009cde" }}>Pal</span>
                </span>
              </div>

              {/* Klarna option */}
              <div
                onClick={() => setSelectedPayment("klarna")}
                className="mam-pay-option mb-3"
                style={{
                  border: `2px solid ${selectedPayment === "klarna" ? "#E04F8C" : "#F5CCE0"}`,
                  background: selectedPayment === "klarna"
                    ? "linear-gradient(135deg, #FDE8F1 0%, #FBD5E6 100%)"
                    : "#FDF4F8",
                  boxShadow: selectedPayment === "klarna"
                    ? "0 4px 18px rgba(224, 79, 140, 0.2)"
                    : "0 2px 6px rgba(0, 0, 0, 0.03)",
                }}
              >
                {selectedPayment === "klarna" && <SelectedBadge />}
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "13.5px",
                    color: "#0F1A2B",
                  }}
                >
                  {lang?.mentorship_pay_with_klarna || "Pay with Klarna"}
                </span>
                <span
                  style={{
                    fontWeight: 850,
                    fontSize: "16px",
                    color: "#111827",
                    letterSpacing: "-0.03em",
                  }}
                >
                  Klarna.
                </span>
              </div>

              {/* Checkboxes */}
              <div
                className="d-flex align-items-start gap-2 mb-3"
                style={{
                  padding: "4px 2px",
                }}
              >
                <div
                  onClick={() => setChecked1((prev) => !prev)}
                  className="mam-checkbox-box"
                  style={{
                    border: `2px solid ${showValidation && checkboxMissing ? "#EF4444" : checked1 ? "#00BADA" : "#CBD5E1"}`,
                    background: checked1 ? "linear-gradient(135deg, #00C8E8 0%, #00BADA 100%)" : "#FFFFFF",
                    boxShadow: checked1 ? "0 2px 8px rgba(0, 186, 218, 0.35)" : "none",
                  }}
                >
                  {checked1 && (
                    <svg width="11" height="9" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#475569",
                    lineHeight: 1.5,
                    fontWeight: 550,
                  }}
                >
                  {lang?.mentorship_accept_prefix || "I accept the"}{" "}
                  <a
                    href="https://medicalradar.es/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#00BADA", fontWeight: 700, textDecoration: "none" }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                  >
                    {lang?.mentorship_privacy_policy_lowercase ||
                      "privacy policy"}
                  </a>{" "}
                  {lang?.mentorship_accept_suffix ||
                    "and the processing of my data for the selection process."}
                </p>
              </div>

              {showValidation && checkboxMissing && (
                <p
                  style={{
                    margin: "-6px 0 12px 28px",
                    fontSize: "11.5px",
                    fontWeight: 650,
                    color: "#EF4444",
                  }}
                >
                  {lang?.mentorship_error_accept_privacy ||
                    "Please accept the privacy policy to continue."}
                </p>
              )}

              {/* Continue button */}
              <button
                type="button"
                onClick={handleContinue}
                disabled={submittingRequest || processingPayment}
                className="mam-cta-btn"
                style={{
                  opacity: submittingRequest || processingPayment ? 0.7 : 1,
                  cursor: submittingRequest || processingPayment ? "wait" : "pointer",
                }}
              >
                <span>
                  {submittingRequest
                    ? lang?.submitting || "Submitting…"
                    : processingPayment
                      ? lang?.mentorship_processing_payment || "Processing…"
                      : mentorPrice > 0
                        ? `${lang?.mentorship_continue_to_payment_button || "Continue to Payment"} - €${mentorPrice.toFixed(2)}`
                        : lang?.mentorship_continue_to_payment_button ||
                          "Continue to Payment"}
                </span>
                {!submittingRequest && !processingPayment && <FaArrowRight size={13} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mentorship Booked! — confirmation shown after Continue succeeds */}
      {(showBookedModal || isBookedClosing) && (
        <div
          className={`mam-curtain-backdrop ${isBookedClosing ? "closing" : ""}`}
          onClick={closeBookedModal}
        >
          <div
            className={`mbk-curtain-content ${isBookedClosing ? "closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="position-relative"
              style={{ padding: "30px 24px 28px" }}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={closeBookedModal}
                className="mam-close-btn"
                aria-label="Close modal"
              >
                <IoClose size={18} />
              </button>

              {/* Success icon with glowing animated halo */}
              <div
                className="d-flex justify-content-center"
                style={{ marginBottom: "18px" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "74px",
                    height: "74px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #D0FFDC 0%, #B4F8C5 100%)",
                    border: "3px solid #FFFFFF",
                    boxShadow: "0 8px 24px rgba(52, 199, 89, 0.25)",
                  }}
                >
                  <svg width="36" height="36" viewBox="0 0 34 34" fill="none">
                    <path
                      d="M7 17L13.5 23.5L27 10"
                      stroke="#15803D"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h5
                style={{
                  fontWeight: 800,
                  fontSize: "21px",
                  color: "#0F1A2B",
                  textAlign: "center",
                  margin: "0 0 6px",
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                {lang?.mentorship_booked_title || "Mentorship Booked!"}
              </h5>

              {/* Subtitle */}
              <p
                style={{
                  fontSize: "12.5px",
                  color: "#475569",
                  textAlign: "center",
                  fontWeight: 500,
                  margin: "0 0 18px",
                  lineHeight: 1.5,
                }}
              >
                {lang?.mentorship_booked_subtitle ||
                  "Your application has been submitted and your spot is confirmed."}
              </p>

              {mentor && (
                <div
                  onClick={() => closeBookedModal(() => navigate(`${NEW_PROFILE_DOCTOR}/${mentor.user_name || mentor.id}`)) }
                  className="mam-mentor-card mb-3"
                >
                  <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, zIndex: 1 }}>
                    <div className="mam-mentor-avatar-wrap">
                      <img
                        src={mentor.image || placeholderAvatar}
                        alt={mentor.name}
                        onError={(e) => {
                          if (e.target.src !== placeholderAvatar) {
                            e.target.src = placeholderAvatar;
                          }
                        }}
                        className="mam-mentor-avatar"
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p
                        className="text-truncate mb-0"
                        style={{
                          fontWeight: 850,
                          fontSize: "15px",
                          color: "#063943",
                          lineHeight: 1.25,
                          letterSpacing: "-0.01em",
                        }}
                        title={mentor.name}
                      >
                        {mentor.name}
                      </p>
                      <p
                        className="text-truncate mb-0"
                        style={{
                          fontSize: "12px",
                          color: "#006D85",
                          fontWeight: 600,
                          lineHeight: 1.35,
                        }}
                        title={mentorSummary(mentorDisplayInfo)}
                      >
                        {mentorSummary(mentorDisplayInfo)}
                      </p>
                    </div>
                  </div>
                  <div className="mam-mentor-chevron">
                    <FaChevronRight size={11} />
                  </div>
                </div>
              )}

              {/* Info box */}
              <div
                style={{
                  background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
                  border: "1.5px solid #FDE68A",
                  borderRadius: "16px",
                  padding: "12px 16px",
                  marginBottom: "20px",
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.08)",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "#92400E",
                    margin: 0,
                    fontWeight: 550,
                    lineHeight: 1.6,
                  }}
                >
                  {lang?.mentorship_contact_info_prefix ||
                    "The mentor will contact you through the"}{" "}
                  <strong style={{ fontWeight: 800, color: "#78350F" }}>
                    {lang?.mentorship_contact_info_bold ||
                      "Ophthalmology Radar in-app chat"}
                  </strong>{" "}
                  {lang?.mentorship_contact_info_suffix ||
                    "within 24 hours to schedule your mentorship session."}
                </p>
              </div>

              {/* Done button */}
              <button
                type="button"
                onClick={closeBookedModal}
                className="mam-cta-btn"
              >
                {lang?.mentorship_done_button || "Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PayPal / Card Drop-in Modal */}
      {dropInModal && (
        <AgendaPaymentDropInModal
          isOpen={dropInModal}
          toggle={() => {
            setDropInModal(false);
            setProcessingPayment(false);
          }}
          title={`Mentorship with ${mentor.name}`}
          formattedPrice={`€${mentorPrice.toFixed(2)}`}
          isInitializing={dropInInitializing}
          isProcessing={processingPayment}
          canPay={canPay}
          onPay={handleDropInPay}
          lang={lang}
        />
      )}

      {/* Klarna Stripe Checkout Modal */}
      <Modal
        isOpen={klarnaModal}
        toggle={() => {
          setKlarnaModal(false);
          setProcessingPayment(false);
        }}
        size="lg"
        centered
        backdrop="static"
      >
        <ModalHeader toggle={() => { setKlarnaModal(false); setProcessingPayment(false); }}>
          {lang?.complete_klarna_payment || "Complete your Klarna payment"}
        </ModalHeader>
        <ModalBody>
          {sessionError && (
            <div className="alert alert-danger" role="alert">
              {sessionError}
            </div>
          )}
          {sessionPaymentStatus && <SessionStatus paymentStatus={sessionPaymentStatus} />}
          {klarnaClientSecret ? (
            <CheckoutProvider clientSecret={klarnaClientSecret}>
              <CheckoutForm
                onPaymentSuccess={(session) => {
                  if (session?.id) {
                    submitMentorRequest({ paymentType: "stripe", nonce: session.id });
                  }
                }}
              />
            </CheckoutProvider>
          ) : (
            <p className="text-center text-muted">
              {lang?.loading_klarna_checkout || "Loading Klarna checkout…"}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => { setKlarnaModal(false); setProcessingPayment(false); }}>
            {lang?.close || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default React.memo(MentorshipApplicationModal);

// import React, { useEffect, useRef, useState } from "react";
// import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
// import { toast } from "react-toastify";
// import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";
// import { RiDeleteBin5Line, RiDeleteBin6Line } from "react-icons/ri";
// import { FaArrowRight } from "react-icons/fa";
// import { apiAdmin, getApiErrorMessage } from "../../services/api";
// import { _imagesURL } from "../../constants/API";
// // Same PayPal (Braintree Drop-in) + Klarna (Stripe embedded Checkout Session)
// // integration used for course purchases — see
// // src/components/course-details/PaypalPaymentPage.jsx for the reference flow.
// import { fetchPaypalToken, generateNonceWithDropIn } from "../../services/paypalApi";
// import { createKlarnaCheckoutSession, getKlarnaCheckoutSessionStatus } from "../../services/stripeApi";
// import { CheckoutProvider } from "../payments/klarna-stripe/CheckoutProvider";
// import { CheckoutForm } from "../payments/klarna-stripe/CheckoutForm";
// import { SessionStatus } from "../payments/klarna-stripe/SessionStatus";

// // Same endpoint ApplyMentorShip.jsx uses — user_id is the mentor's id, and
// // the actual profile is nested one level deeper under data.data.mentor.
// async function fetchMentorProfile(userId) {
//   const res = await apiAdmin.post("user/mentor/profile", { user_id: userId });
//   return res.data;
// }

// // Used only if SplashData hasn't loaded into localStorage yet.
// const FALLBACK_ACCOUNT_DETAILS = {
//   fullName: "Dr. Jane Smith",
//   workEmail: "janesmith@hospital.com",
//   image: placeholderAvatar,
// };

// // Same SplashData.user source CreateMentorship.jsx reads from — the
// // currently logged-in doctor applying, not the mentor being applied to.
// function getAccountDetails() {
//   try {
//     const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
//     if (!user) return FALLBACK_ACCOUNT_DETAILS;
//     return {
//       fullName: user.name || [user.firstname, user.lastname].filter(Boolean).join(" ") || FALLBACK_ACCOUNT_DETAILS.fullName,
//       workEmail: user.email || FALLBACK_ACCOUNT_DETAILS.workEmail,
//       image: user.image ? `${_imagesURL}${user.image}` : FALLBACK_ACCOUNT_DETAILS.image,
//     };
//   } catch (e) {
//     return FALLBACK_ACCOUNT_DETAILS;
//   }
// }

// // How long an upload can run before we switch from the normal "Uploading…"
// // indicator to the "Upload Delay" state (per Figma: taking longer than
// // expected / stuck in progress for an extended period).
// const UPLOAD_DELAY_THRESHOLD_MS = 6000;

// function CheckSvg() {
//   return (
//     <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
//       <path
//         d="M1 4L3.5 6.5L9 1"
//         stroke="#00BADA"
//         strokeWidth="1.8"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

// // Small green circular checkmark badge — pinned to the top-right corner of
// // whichever payment option is currently selected, overlapping the option's
// // own border/edge like a "selected" pin.
// function SelectedBadge() {
//   return (
//     <span
//       className="d-flex align-items-center justify-content-center"
//       style={{
//         position: "absolute",
//         top: "-9px",
//         right: "0px",
//         width: "20px",
//         height: "20px",
//         borderRadius: "50%",
//         background: "#34C759",
//         border: "1.5px solid #0612234D",
//         boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
//       }}
//     >
//       <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
//         <path
//           d="M1 4L3.5 6.5L9 1"
//           stroke="#111"
//           strokeWidth="1.8"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </span>
//   );
// }

// // Rounded file/document icon — light green pill background, matches the
// // "uploaded" card look from the design.
// function CvFileIcon() {
//   return (
//     <div
//       style={{
//         width: "38px",
//         height: "38px",
//         borderRadius: "10px",
//         background: "#E6F7EE",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flexShrink: 0,
//       }}
//     >
//       <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
//         <path
//           d="M13 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-6Z"
//           stroke="#1F9D55"
//           strokeWidth="1.7"
//           strokeLinejoin="round"
//         />
//         <path
//           d="M13 2v5a1 1 0 0 0 1 1h5"
//           stroke="#1F9D55"
//           strokeWidth="1.7"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </div>
//   );
// }

// // Same rounded slot, but amber-tinted — used for the "delayed" state so it
// // reads as a soft warning rather than a normal in-progress spinner.
// function CvDelayedIcon() {
//   return (
//     <div
//       style={{
//         width: "38px",
//         height: "38px",
//         borderRadius: "10px",
//         background: "#FEF3E2",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flexShrink: 0,
//       }}
//     >
//       <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
//         <circle cx="12" cy="12" r="9" stroke="#B45309" strokeWidth="1.7" />
//         <path
//           d="M12 7v5l3 2"
//           stroke="#B45309"
//           strokeWidth="1.7"
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </div>
//   );
// }

// // Small rotating ring — used inside both the uploading and delayed cards.
// function SpinnerIcon({ color = "#5F6673", size = 14 }) {
//   return (
//     <>
//       <style>{`@keyframes cvUploadSpin{to{transform:rotate(360deg)}}`}</style>
//       <span
//         style={{
//           display: "inline-block",
//           width: `${size}px`,
//           height: `${size}px`,
//           border: `2px solid ${color}33`,
//           borderTopColor: color,
//           borderRadius: "50%",
//           animation: "cvUploadSpin 0.7s linear infinite",
//           flexShrink: 0,
//         }}
//       />
//     </>
//   );
// }

// function TrashIcon() {
//   return (
//     <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
//       <path
//         d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13"
//         stroke="#E24C4C"
//         strokeWidth="1.7"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

// function CancelIcon() {
//   return (
//     <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
//       <path
//         d="M6 6l12 12M18 6 6 18"
//         stroke="#5F6673"
//         strokeWidth="1.8"
//         strokeLinecap="round"
//       />
//     </svg>
//   );
// }

// // "Uploaded just now" / "Uploaded 4 min ago" / "Uploaded 2 hours ago" —
// // recomputed live off uploadedAt so it never goes stale while the modal
// // stays open.
// function formatUploadedAgo(uploadedAt) {
//   if (!uploadedAt) return "";
//   const seconds = Math.floor((Date.now() - uploadedAt) / 1000);
//   if (seconds < 45) return "Uploaded just now";
//   const minutes = Math.floor(seconds / 60);
//   if (minutes < 60) return `Uploaded ${minutes} min${minutes > 1 ? "s" : ""} ago`;
//   const hours = Math.floor(minutes / 60);
//   if (hours < 24) return `Uploaded ${hours} hour${hours > 1 ? "s" : ""} ago`;
//   const days = Math.floor(hours / 24);
//   return `Uploaded ${days} day${days > 1 ? "s" : ""} ago`;
// }

// function mentorSummary(mentor) {
//   if (!mentor) return "";
//   return [mentor.specialty, mentor.institution, mentor.institutionSub].filter(Boolean).join(" · ");
// }

// function MentorshipApplicationModal({ isOpen, toggle, mentor, onSubmitted }) {
//   const [checked1, setChecked1] = useState(true);
//   const [checked2, setChecked2] = useState(false);
//   const [selectedPayment, setSelectedPayment] = useState("paypal");
//   const [dragOver, setDragOver] = useState(false);
//   const [mentorProfileData, setMentorProfileData] = useState(null);
//   const [submittingRequest, setSubmittingRequest] = useState(false);
//   // Payment step — mirrors PaypalPaymentPage.jsx's Braintree Drop-in /
//   // Stripe embedded Checkout Session flow.
//   const [processingPayment, setProcessingPayment] = useState(false);
//   const [dropInModal, setDropInModal] = useState(false);
//   const [klarnaModal, setKlarnaModal] = useState(false);
//   const [klarnaClientSecret, setKlarnaClientSecret] = useState(null);
//   const [sessionError, setSessionError] = useState(null);
//   const [sessionPaymentStatus, setSessionPaymentStatus] = useState(null);
//   const accountDetails = getAccountDetails();

//   useEffect(() => {
//     if (!isOpen || !mentor?.id) {
//       setMentorProfileData(null);
//       return;
//     }
//     let cancelled = false;
//     fetchMentorProfile(mentor.id)
//       .then((data) => {
//         if (!cancelled && data?.status) setMentorProfileData(data.data?.mentor || null);
//       })
//       .catch(() => {});
//     return () => {
//       cancelled = true;
//     };
//   }, [isOpen, mentor?.id]);

//   // Real "Receive CV with Mentorship Requests" setting from this specific
//   // mentor's profile — the CV section only shows when they've turned it on.
//   const mentorProfile = mentorProfileData?.mentor_profile;
//   const showCvUpload = mentorProfile?.is_receive_data === 1;
//   const cvDropzoneText = showCvUpload && mentorProfile?.receive_data_title
//     ? mentorProfile.receive_data_title
//     : null;

//   const [showBookedModal, setShowBookedModal] = useState(false);

//   const [cvFile, setCvFile] = useState(null);
//   // "idle" -> no file yet, dropzone visible
//   // "uploading" -> file picked, upload in flight, within normal time window
//   // "delayed" -> still uploading past UPLOAD_DELAY_THRESHOLD_MS
//   // "uploaded" -> upload finished, file card with delete/replace shown
//   const [cvUploadState, setCvUploadState] = useState("idle");
//   const [uploadedAt, setUploadedAt] = useState(null);
//   // Forces a re-render every 30s so "Uploaded just now" keeps advancing
//   // ("1 min ago", "2 mins ago"...) without needing any other state change.
//   const [, forceTick] = useState(0);

//   const delayTimerRef = useRef(null);
//   const uploadTimerRef = useRef(null);
//   const tickIntervalRef = useRef(null);

//   const clearUploadTimers = () => {
//     if (delayTimerRef.current) {
//       clearTimeout(delayTimerRef.current);
//       delayTimerRef.current = null;
//     }
//     if (uploadTimerRef.current) {
//       clearTimeout(uploadTimerRef.current);
//       uploadTimerRef.current = null;
//     }
//   };

//   useEffect(() => {
//     return () => clearUploadTimers();
//   }, []);

//   // Keep the relative "Uploaded X ago" label fresh while a file sits uploaded.
//   useEffect(() => {
//     if (cvUploadState !== "uploaded") return;
//     tickIntervalRef.current = setInterval(() => forceTick((n) => n + 1), 30000);
//     return () => clearInterval(tickIntervalRef.current);
//   }, [cvUploadState]);

//   // Klarna's checkout can redirect the browser away and back (e.g. its own
//   // financing approval step) — this picks the flow back up on return via
//   // ?session_id= in the URL, same pattern as PaypalPaymentPage.jsx. Must sit
//   // above the `if (!mentor) return null` below — hooks can't be conditional.
//   useEffect(() => {
//     const params = new URLSearchParams(window.location.search);
//     const sessionId = params.get("session_id");
//     const pending = localStorage.getItem("mam_pending_request");
//     if (!sessionId || !pending || !mentor?.id) return;

//     setProcessingPayment(true);
//     getKlarnaCheckoutSessionStatus(sessionId)
//       .then((res) => {
//         const sessionData = res?.data?.data ?? res?.data;
//         const paymentStatus = sessionData?.payment_status ?? res?.payment_status;
//         setSessionPaymentStatus(paymentStatus);
//         if (paymentStatus === "paid") {
//           return submitMentorRequest({ paymentType: "stripe", nonce: sessionId });
//         }
//         throw new Error(`Payment was not completed (status: ${paymentStatus ?? "unknown"}).`);
//       })
//       .catch((err) => {
//         console.error("Klarna session verification error:", err);
//         setSessionError(err?.response?.data?.message || err.message || "Failed to verify payment.");
//         setProcessingPayment(false);
//       });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [mentor?.id]);

//   // Placeholder for the real upload call — swap this out for the actual
//   // API request. Kept as its own function so wiring up a real endpoint
//   // later is a one-line change; everything else (delay detection, state
//   // transitions, cleanup) stays the same.
//   const uploadCvFile = (file) => {
//     return new Promise((resolve) => {
//       const duration = 1500 + Math.random() * 2000;
//       uploadTimerRef.current = setTimeout(resolve, duration);
//     });
//   };

//   const startUpload = (file) => {
//     if (!file) return;
//     clearUploadTimers();
//     setCvFile(file);
//     setCvUploadState("uploading");
//     setUploadedAt(null);

//     // Flip to the "delayed" visual if the upload is still going after the
//     // threshold — this only changes what's shown, the upload itself keeps
//     // running underneath.
//     delayTimerRef.current = setTimeout(() => {
//       setCvUploadState((prev) => (prev === "uploading" ? "delayed" : prev));
//     }, UPLOAD_DELAY_THRESHOLD_MS);

//     uploadCvFile(file).then(() => {
//       if (delayTimerRef.current) {
//         clearTimeout(delayTimerRef.current);
//         delayTimerRef.current = null;
//       }
//       setCvUploadState("uploaded");
//       setUploadedAt(Date.now());
//     });
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files?.[0] || null;
//     // Reset the input so selecting the same filename again after a delete
//     // still fires onChange.
//     e.target.value = "";
//     if (file) startUpload(file);
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     setDragOver(false);
//     if (cvUploadState !== "idle") return; // a file is already in flight/uploaded
//     const file = e.dataTransfer.files?.[0];
//     if (file) startUpload(file);
//   };

//   // Cancels an in-flight upload (uploading/delayed) — back to the empty
//   // dropzone, nothing kept.
//   const handleCancelUpload = () => {
//     clearUploadTimers();
//     setCvFile(null);
//     setCvUploadState("idle");
//     setUploadedAt(null);
//   };

//   // Removes a completed upload — this is the only way back to the empty
//   // dropzone once a file has finished uploading, so a new PDF can't be
//   // picked while one is already attached.
//   const handleDeleteCv = () => {
//     clearUploadTimers();
//     setCvFile(null);
//     setCvUploadState("idle");
//     setUploadedAt(null);
//   };

//   if (!mentor) return null;

//   // The mentor's own asking price, set on their application form
//   // (CreateMentorship.jsx's "price" field) — same field, just not shown
//   // anywhere on the mentee side until now.
//   const mentorPrice = Number(mentorProfile?.price ?? mentor.mentor_profile?.price ?? 0) || 0;

//   // --- Previous behavior (kept for reference, not removed) ---
//   // "Continue to Payment" used to submit the mentor/request straight away,
//   // with no real payment step in between despite the PayPal/Klarna cards
//   // already being selectable above. Replaced below by a real payment step
//   // (Braintree Drop-in for PayPal, Stripe embedded Checkout for Klarna) that
//   // automatically fires this same request once payment succeeds.
//   //
//   // const handleContinue = async () => {
//   //   if (!checked1) {
//   //     toast.error("Please accept the privacy policy to continue.");
//   //     return;
//   //   }
//   //   setSubmittingRequest(true);
//   //   try {
//   //     const mentorProfileId = mentorProfile?.id ?? mentor.mentor_profile?.id;
//   //     const fd = new FormData();
//   //     fd.append("mentor_id", mentor.id);
//   //     fd.append("mentorship_type", "One-To-One");
//   //     if (cvUploadState === "uploaded" && cvFile) {
//   //       fd.append("data", cvFile);
//   //     }
//   //     const res = await apiAdmin.post("user/mentor/request", fd, {
//   //       headers: { "Content-Type": "multipart/form-data" },
//   //     });
//   //     if (res.data?.status) {
//   //       onSubmitted?.(mentor);
//   //       toggle();
//   //       setShowBookedModal(true);
//   //     } else {
//   //       toast.error(getApiErrorMessage(res.data, "Couldn't submit your request. Please try again."));
//   //     }
//   //   } catch (err) {
//   //     toast.error(getApiErrorMessage(err.response?.data, "Couldn't submit your request. Please try again."));
//   //   } finally {
//   //     setSubmittingRequest(false);
//   //   }
//   // };

//   // Actually submits the mentor/request — same endpoint/payload as before,
//   // now called automatically right after a payment succeeds (PayPal nonce
//   // or Klarna/Stripe session id attached as paymentMeta).
//   const submitMentorRequest = async (paymentMeta) => {
//     setSubmittingRequest(true);
//     try {
//       const fd = new FormData();
//       fd.append("mentor_id", mentor.id);
//       fd.append("mentorship_type", "One-To-One");
//       if (cvUploadState === "uploaded" && cvFile) {
//         fd.append("data", cvFile);
//       }
//       if (paymentMeta?.paymentType) fd.append("payment_type", paymentMeta.paymentType);
//       if (paymentMeta?.nonce) fd.append("nonce", paymentMeta.nonce);
//       const res = await apiAdmin.post("user/mentor/reqwuest", fd, {
//         headers: { "Content-Type": "multipart/form-data" },
//       });
//       if (res.data?.status) {
//         localStorage.removeItem("mam_pending_request");
//         onSubmitted?.(mentor);
//         setDropInModal(false);
//         setKlarnaModal(false);
//         toggle();
//         setShowBookedModal(true);
//       } else {
//         toast.error(getApiErrorMessage(res.data, "Couldn't submit your request. Please try again."));
//       }
//     } catch (err) {
//       toast.error(getApiErrorMessage(err.response?.data, "Couldn't submit your request. Please try again."));
//     } finally {
//       setSubmittingRequest(false);
//       setProcessingPayment(false);
//     }
//   };

//   // ── PayPal / Card — Braintree Drop-in, same as PaypalPaymentPage.jsx ──────
//   const handlePaypalPayment = async () => {
//     setProcessingPayment(true);
//     try {
//       if (mentorPrice > 0) {
//         const userToken = await fetchPaypalToken();
//         setDropInModal(true);
//         const nonce = await generateNonceWithDropIn(userToken, "mam-dropin-container", mentorPrice.toFixed(2));
//         await submitMentorRequest({ paymentType: "paypal", nonce });
//       } else {
//         // Free mentorship (no price set) — nothing to tokenize.
//         await submitMentorRequest({ paymentType: "paypal" });
//       }
//     } catch (err) {
//       console.error("PayPal payment error:", err);
//       toast.error("Couldn't process PayPal payment. Please try again.");
//       setDropInModal(false);
//       setProcessingPayment(false);
//     }
//   };

//   // ── Klarna — Stripe embedded Checkout Session, same as PaypalPaymentPage.jsx ──
//   const handleKlarnaPayment = async () => {
//     setProcessingPayment(true);
//     setSessionError(null);
//     try {
//       // Persisted so the redirect-return effect below can finish this
//       // request even after Klarna sends the browser away and back.
//       localStorage.setItem(
//         "mam_pending_request",
//         JSON.stringify({ mentorId: mentor.id })
//       );
//       const amount = Math.round(mentorPrice * 100);
//       const response = await createKlarnaCheckoutSession({
//         courseId: mentor.id,
//         amount,
//         title: `Mentorship with ${mentor.name}`,
//       });
//       const clientSecret = response?.data?.client_secret ?? response?.client_secret;
//       if (clientSecret) {
//         setKlarnaClientSecret(clientSecret);
//         setKlarnaModal(true);
//       } else {
//         throw new Error("Could not initialize Klarna checkout.");
//       }
//     } catch (err) {
//       console.error("Klarna payment error:", err);
//       toast.error("Couldn't start Klarna checkout. Please try again.");
//       setProcessingPayment(false);
//     }
//   };

//   const handleContinue = () => {
//     if (!checked1) {
//       toast.error("Please accept the privacy policy to continue.");
//       return;
//     }
//     if (selectedPayment === "klarna") {
//       handleKlarnaPayment();
//     } else {
//       handlePaypalPayment();
//     }
//   };

//   const closeBookedModal = () => {
//     setShowBookedModal(false);
//   };

//   return (
//     <>
//     <Modal
//       isOpen={isOpen}
//       toggle={toggle}
//       centered
//       className="mentorship-page mam-modal-dialog"
//       contentClassName="mam-modal-content"
//     >
//       <style>{`
//         .mam-modal-content {
//           border: none;
//           border-radius: 18px;
//           overflow: hidden;
//           box-shadow: 0 20px 60px rgba(0,0,0,0.2);
//         }
//         .mam-modal-dialog {
//           max-width: 540px;
//         }
//       `}</style>
//       <ModalBody className="position-relative" style={{ padding: "28px 28px 32px" }}>
//         {/* Close button */}
//         <button
//           type="button"
//           onClick={toggle}
//           className="border-0 bg-transparent"
//           style={{
//             position: "absolute",
//             top: "16px",
//             right: "16px",
//             fontSize: "1.1rem",
//             cursor: "pointer",
//             color: "#6b7280",
//             lineHeight: 1,
//             padding: "4px",
//           }}
//         >
//           ✕
//         </button>

//         {/* Header */}
//         <h5 style={{ fontWeight: 700, fontSize: "20px", color: "#0E1421", margin: "0 0 4px" }}>
//           Apply for mentorship
//         </h5>
//         <p style={{ color: "#5F6673", fontSize: "11px", fontWeight:"500", margin: "0 0 22px" }}>
//           Review your details, then choose how you'd like to pay.
//         </p>

//         {/* SELECTED MENTOR */}
//         <div className="d-flex justify-content-between align-items-center mb-2">
//           <span style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em" }}>
//             SELECTED MENTOR
//           </span>
//           <button
//             type="button"
//             onClick={toggle}
//             style={{
//               border: "1px solid #E6E7EA",
//               borderRadius: "20px",
//               padding: "3px 12px",
//               background: "none",
//               fontSize: "10px",
//               fontWeight: 500,
//               color: "#0E1421",
//               cursor: "pointer",
//             }}
//           >
//             Change
//           </button>
//         </div>

//         <div
//           className="d-flex align-items-start"
//           style={{
//             background: "#E8FBFF",
//             borderRadius: "20px",
//             padding: "14px",
//             border:"1px solid #D7E0EA",
//             gap: "12px",
//             marginBottom: "20px",
//           }}
//         >
//           <img
//             src={mentor.image}
//             alt={mentor.name}
//             style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//           />
//           <div>
//             <p style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", margin: "0 0 3px" }}>
//               {mentor.name}
//             </p>
//             <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
//               {mentorSummary(mentor)}
//             </p>
//           </div>
//         </div>

//         {/* YOUR DETAILS */}
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em", marginBottom: "8px" }}
//         >
//           YOUR DETAILS
//         </span>

//         <div
//           className="d-flex align-items-center"
//           style={{ border: "1px solid #D7E0EA", borderRadius: "20px", padding: "14px", gap: "12px", marginBottom: "20px" }}
//         >
//           <img
//             src={accountDetails.image}
//             alt={accountDetails.fullName}
//             style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//           />
//           <div>
//             <p style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", margin: "0 0 2px" }}>
//               {accountDetails.fullName}
//             </p>
//             <p style={{ fontSize: "0.78rem", color: "#6b7280", margin: 0 }}>
//               {accountDetails.workEmail}
//             </p>
//           </div>
//         </div>

//         {/* UPLOAD YOUR CV — only when the mentor has this enabled in settings */}
//         {showCvUpload && (
//           <>
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#031316", letterSpacing: "0.1em", marginBottom: "8px" }}
//         >
//           UPLOAD YOUR CV
//         </span>

//         {cvUploadState === "idle" ? (
//           <div
//             onDragOver={(e) => {
//               e.preventDefault();
//               setDragOver(true);
//             }}
//             onDragLeave={() => setDragOver(false)}
//             onDrop={handleDrop}
//             onClick={() => document.getElementById("mamCvInput")?.click()}
//             style={{
//               border: `1.5px dashed ${dragOver ? "#0ea5e9" : "#d1d5db"}`,
//               borderRadius: "12px",
//               padding: "28px 20px",
//               textAlign: "center",
//               marginBottom: "20px",
//               background: "#fff",
//               transition: "all 0.2s",
//               cursor: "pointer",
//             }}
//           >
//             <p style={{ margin: "0 0 4px", fontSize: "13px", fontWeight:"400", color: "#0E1421" }}>
//               {cvDropzoneText || (
//                 <>
//                   Drag &amp; drop your CV here, or{" "}
//                   <span style={{ color: "#1DC1DD", textDecoration: "underline" }}>browse files</span>
//                 </>
//               )}
//             </p>
//             <p style={{ margin: 0, fontSize: "11px", fontWeight:"400", color: "#5F6673" }}>PDF format</p>
//             <input
//               id="mamCvInput"
//               type="file"
//               accept=".pdf"
//               style={{ display: "none" }}
//               onChange={handleFileChange}
//             />
//           </div>
//         ) : (
//           <div
//             className="d-flex align-items-center justify-content-between"
//             style={{
//               border: `1px solid ${cvUploadState === "delayed" ? "#F4D9A8" : "#E6E7EA"}`,
//               borderRadius: "14px",
//               padding: "12px 14px",
//               marginBottom: "20px",
//               gap: "12px",
//               background: "#fff",
//             }}
//           >
//             <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0 }}>
//               {cvUploadState === "delayed" ? <CvDelayedIcon /> : <CvFileIcon />}
//               <div style={{ minWidth: 0 }}>
//                 <p
//                   style={{
//                     margin: "0 0 2px",
//                     fontSize: "13px",
//                     fontWeight: 600,
//                     color: "#0E1421",
//                     whiteSpace: "nowrap",
//                     overflow: "hidden",
//                     textOverflow: "ellipsis",
//                   }}
//                   title={cvFile?.name}
//                 >
//                   {cvFile?.name}
//                 </p>
//                 <p
//                   className="d-flex align-items-center"
//                   style={{ margin: 0, fontSize: "11px", color: "#5F6673", gap: "6px" }}
//                 >
//                   {cvUploadState === "uploading" && (
//                     <>
//                       <SpinnerIcon />
//                       <span>PDF format &nbsp;•&nbsp; Uploading…</span>
//                     </>
//                   )}
//                   {cvUploadState === "delayed" && (
//                     <>
//                       <SpinnerIcon color="#B45309" />
//                       <span style={{ color: "#B45309" }}>
//                         Still uploading — this is taking longer than usual
//                       </span>
//                     </>
//                   )}
//                   {cvUploadState === "uploaded" && (
//                     <span>PDF format &nbsp;•&nbsp; {formatUploadedAgo(uploadedAt)}</span>
//                   )}
//                 </p>
//               </div>
//             </div>

//             {(cvUploadState === "uploading" || cvUploadState === "delayed") && (
//               <button
//                 type="button"
//                 onClick={handleCancelUpload}
//                 title="Cancel upload"
//                 className="d-flex align-items-center justify-content-center border-0"
//                 style={{
//                   width: "34px",
//                   height: "34px",
//                   borderRadius: "50%",
//                   background: "#F3F4F6",
//                   flexShrink: 0,
//                   cursor: "pointer",
//                 }}
//               >
//                 <CancelIcon />
//               </button>
//             )}

//             {cvUploadState === "uploaded" && (
//               <button
//                 type="button"
//                 onClick={handleDeleteCv}
//                 title="Remove PDF"
//                 className="d-flex align-items-center justify-content-center border-0"
//                 style={{
//                   width: "34px",
//                   height: "34px",
//                   borderRadius: "50%",
//                   background: "#FF383C1A",
//                   color:"red",
//                   flexShrink: 0,
//                   cursor: "pointer",
//                 }}
//               >
//               <RiDeleteBin6Line />

//               </button>
//             )}
//           </div>
//         )}
//           </>
//         )}

//         {/* PAYMENT METHOD */}
//         <span
//           className="d-block"
//           style={{ fontSize: "11px", fontWeight: 500, color: "#0E1421", letterSpacing: "0.1em", marginBottom: "10px" }}
//         >
//           PAYMENT METHOD
//         </span>

//         {/* PayPal option */}
// <div
//   onClick={() => setSelectedPayment("paypal")}
//   className="d-flex justify-content-between align-items-center position-relative"
//   style={{
//     border: `2px solid ${selectedPayment === "paypal" ? "#061223" : "#FCD34D"}`,
//     borderRadius: "20px",
//     padding: "8px 16px",
//     marginBottom: "10px",
//     cursor: "pointer",
//     background: "#FCD34D",
//     transition: "all 0.2s",
//     overflow: "visible",
//   }}
// >
//   {selectedPayment === "paypal" && <SelectedBadge />}
//   <span style={{ fontWeight: 500, fontSize: "0.88rem", color: "#111827" }}>
//     Pay with card or PayPal
//   </span>
//   <span style={{ fontWeight: 800, fontSize: "0.92rem", fontStyle: "italic" }}>
//     <span style={{ color: "#003087" }}>Pay</span>
//     <span style={{ color: "#009cde" }}>Pal</span>
//   </span>
// </div>

// {/* Klarna option */}
// <div
//   onClick={() => setSelectedPayment("klarna")}
//   className="d-flex justify-content-between align-items-center position-relative"
//   style={{
//     border: `2px solid ${selectedPayment === "klarna" ? "#061223" : "#F3B5D3"}`,
//     borderRadius: "20px",
//     padding: "8px 16px",
//     marginBottom: "18px",
//     cursor: "pointer",
//     background: "#F3B5D3",
//     transition: "all 0.2s",
//     overflow: "visible",
//   }}
// >
//   {selectedPayment === "klarna" && <SelectedBadge />}
//   <span style={{ fontWeight: 500, fontSize: "0.88rem", color: "#111827" }}>
//     Pay with Klarna
//   </span>
//   <span style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", letterSpacing: "-0.02em" }}>
//     Klarna.
//   </span>
// </div>

//         {/* Checkboxes */}
//         <div className="d-flex align-items-start" style={{ gap: "10px", marginBottom: "12px" }}>
//           <div
//             onClick={() => setChecked1((prev) => !prev)}
//             style={{
//               width: "18px",
//               height: "18px",
//               borderRadius: "4px",
//               border: `2px solid ${checked1 ? "#00BADA" : "#00BADA"}`,
//               background: checked1 ? "#1DC1DD33" : "#fff",
//               flexShrink: 0,
//               marginTop: "1px",
//               color:"#00BADA",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               cursor: "pointer",
//               transition: "all 0.15s",
//             }}
//           >
//             {checked1 && <CheckSvg />}
//           </div>
//           <p style={{ margin: 0, fontSize: "12px", color: "#5F6673", lineHeight: 1.5 }}>
//             I accept the{" "}
//             <a
//               href="https://medicalradar.es/privacy-policy"
//               target="_blank"
//               rel="noopener noreferrer"
//               style={{ color: "#1DC1DD", }}
//             >
//               privacy policy
//             </a>{" "}
//             and the processing of my data for the selection process.
//           </p>
//         </div>

//         {/* Continue button */}
//         <button
//           type="button"
//           onClick={handleContinue}
//           disabled={submittingRequest || processingPayment}
//           className="d-flex align-items-center justify-content-center w-100 border-0 text-white"
//           style={{
//             gap: "8px",
//             background: "#00BADA",
//             borderRadius: "30px",
//             padding: "14px",
//             fontWeight: 700,
//             fontSize: "16px",
//             cursor: "pointer",
//             letterSpacing: "0.01em",
//             opacity: submittingRequest || processingPayment ? 0.7 : 1,
//           }}
//         >
//           {submittingRequest
//             ? "Submitting…"
//             : processingPayment
//             ? "Processing payment…"
//             : "Continue to Payment"}
//         {!submittingRequest && !processingPayment && <FaArrowRight />}

//         </button>
//       </ModalBody>
//     </Modal>

//     {/* PayPal Drop-in Modal */}
//     <Modal
//       isOpen={dropInModal}
//       toggle={() => {
//         setDropInModal(false);
//         setProcessingPayment(false);
//       }}
//       size="lg"
//       centered
//       backdrop="static"
//     >
//       <ModalHeader toggle={() => setDropInModal(false)}>Payment</ModalHeader>
//       <ModalBody>
//         <div id="mam-dropin-container"></div>
//       </ModalBody>
//       <ModalFooter>
//         <Button
//           color="secondary"
//           onClick={() => {
//             setDropInModal(false);
//             setProcessingPayment(false);
//           }}
//         >
//           Close
//         </Button>
//       </ModalFooter>
//     </Modal>

//     {/* Klarna Stripe Checkout Modal */}
//     <Modal isOpen={klarnaModal} toggle={() => setKlarnaModal(false)} size="lg" centered backdrop="static">
//       <ModalHeader toggle={() => setKlarnaModal(false)}>Complete your Klarna payment</ModalHeader>
//       <ModalBody>
//         {sessionError && <div className="alert alert-danger mb-3">{sessionError}</div>}
//         {sessionPaymentStatus && <SessionStatus paymentStatus={sessionPaymentStatus} />}
//         {klarnaClientSecret ? (
//           <CheckoutProvider clientSecret={klarnaClientSecret}>
//             <CheckoutForm
//               onPaymentSuccess={(session) => {
//                 // Embedded checkout finalized without a browser redirect.
//                 if (session?.id) {
//                   submitMentorRequest({ paymentType: "stripe", nonce: session.id });
//                 }
//               }}
//             />
//           </CheckoutProvider>
//         ) : (
//           <p className="text-center text-muted">Loading Klarna checkout…</p>
//         )}
//       </ModalBody>
//       <ModalFooter>
//         <Button color="secondary" onClick={() => setKlarnaModal(false)}>
//           Close
//         </Button>
//       </ModalFooter>
//     </Modal>

//     {/* Mentorship Booked! — confirmation shown after Continue succeeds */}
//     <Modal
//       isOpen={showBookedModal}
//       toggle={closeBookedModal}
//       centered
//       className="mentorship-page mbk-modal-dialog"
//       contentClassName="mbk-modal-content"
//     >
//       <style>{`
//         .mbk-modal-content {
//           border: none;
//           border-radius: 20px;
//           overflow: hidden;
//           box-shadow: 0 24px 64px rgba(0,0,0,0.25);
//         }
//         .mbk-modal-dialog {
//           max-width: 520px;
//         }
//       `}</style>
//       <ModalBody className="position-relative" style={{ padding: "32px 28px 28px" }}>
//         {/* Close button */}
//         <button
//           type="button"
//           onClick={closeBookedModal}
//           className="border-0 bg-transparent"
//           style={{
//             position: "absolute",
//             top: "16px",
//             right: "16px",
//             fontSize: "1.1rem",
//             color: "#6b7280",
//             cursor: "pointer",
//             lineHeight: 1,
//             padding: "4px 6px",
//           }}
//         >
//           ✕
//         </button>

//         {/* Success icon */}
//         <div className="d-flex justify-content-center" style={{ marginBottom: "20px" }}>
//           <div
//             className="d-flex align-items-center justify-content-center"
//             style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#D0FFDC" }}
//           >
//             <svg width="54" height="54" viewBox="0 0 34 34" fill="none">
//               <path
//                 d="M7 17L13.5 23.5L27 10"
//                 stroke="#34C759"
//                 strokeWidth="4"
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//               />
//             </svg>
//           </div>
//         </div>

//         {/* Title */}
//         <h5
//           style={{
//             fontWeight: 700,
//             fontSize: "20px",
//             color: "#0E1421",
//             textAlign: "center",
//             margin: "0 0 8px",
//           }}
//         >
//           Mentorship Booked!
//         </h5>

//         {/* Subtitle */}
//         <p
//           style={{
//             fontSize: "12px",
//             color: "#5F6673",
//             textAlign: "center",
//             fontWeight:"500",
//             margin: "0 0 22px",
//             lineHeight: 1.5,
//           }}
//         >
//           Your application has been submitted and your spot is confirmed.
//         </p>

//         {mentor && (
//           <div
//             className="d-flex align-items-start"
//             style={{
//               background: "#E8FBFF",
//               borderRadius: "20px",
//               border:"1px solid #D7E0EA",
//               padding: "14px",
//               gap: "13px",
//               marginBottom: "14px",
//             }}
//           >
//             <img
//               src={mentor.image}
//               alt={mentor.name}
//               style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
//             />
//             <div>
//               <p style={{ fontWeight: 500, fontSize: "14px", color: "#111827", margin: "0 0 4px" }}>
//                 {mentor.name}
//               </p>
//               <p style={{ fontSize: "12px", color: "#5F6673", margin: 0, lineHeight: 1.5 }}>
//                 {mentorSummary(mentor)}
//               </p>
//             </div>
//           </div>
//         )}

//         {/* Info box */}
//         <div
//           style={{
//             background: "#FFEFAD",
//             border: "1px solid #FFCC00",
//             borderRadius: "12px",
//             padding: "6px 16px",
//             marginBottom: "22px",
//             textAlign: "center",
//           }}
//         >
//           <p style={{ fontSize: "12px", color: "#0E1421", margin: 0, fontWeight:"500", lineHeight: 1.6 }}>
//             The mentor will contact you through the{" "}
//             <strong style={{ fontWeight: 700, color: "#111827" }}>
//               Ophthalmology Radar in-app chat
//             </strong>{" "}
//             within 24 hours to schedule your mentorship session.
//           </p>
//         </div>

//         {/* Done button */}
//         <button
//           type="button"
//           onClick={closeBookedModal}
//           className="d-block w-100 border-0 text-white"
//           style={{
//             background: "#00BADA",
//             borderRadius: "30px",
//             padding: "14px",
//             fontWeight: 600,
//             fontSize: "0.95rem",
//             cursor: "pointer",
//             letterSpacing: "0.01em",
//           }}
//         >
//           Done
//         </button>
//       </ModalBody>
//     </Modal>
//     </>
//   );
// }

// export default React.memo(MentorshipApplicationModal);
